import { Document } from "mongodb";

import { ProcessingInvalidStatusTransitionError } from "./processing-errors";
import { ProcessingRuntimeError } from "./processing-runtime-error";

export enum ProcessingStatus {
  IDLE = "IDLE",
  PROCESSING = "PROCESSING",
  DELETING = "DELETING",
  PROCESSING_ERROR = "PROCESSING_ERROR",
  RUNTIME_ERROR = "RUNTIME_ERROR",
  CANCELLING = "CANCELLING",
}

const VALID_STATUS_TRANSITIONS: Record<ProcessingStatus, ProcessingStatus[]> = {
  [ProcessingStatus.IDLE]: [ProcessingStatus.PROCESSING, ProcessingStatus.DELETING],
  [ProcessingStatus.PROCESSING]: [
    ProcessingStatus.CANCELLING,
    ProcessingStatus.PROCESSING_ERROR,
    ProcessingStatus.RUNTIME_ERROR,
    ProcessingStatus.IDLE,
  ],
  [ProcessingStatus.DELETING]: [
    ProcessingStatus.IDLE,
    ProcessingStatus.PROCESSING_ERROR,
    ProcessingStatus.RUNTIME_ERROR,
  ],
  [ProcessingStatus.PROCESSING_ERROR]: [
    ProcessingStatus.IDLE,
    ProcessingStatus.PROCESSING,
    ProcessingStatus.DELETING,
    ProcessingStatus.PROCESSING_ERROR,
  ], // when we have multiple data entries with errors, processing error is a valid state to transition to
  [ProcessingStatus.RUNTIME_ERROR]: [ProcessingStatus.DELETING, ProcessingStatus.CANCELLING],
  [ProcessingStatus.CANCELLING]: [ProcessingStatus.IDLE, ProcessingStatus.RUNTIME_ERROR],
};

export class ProcessingSnapshot {
  protected _status: ProcessingStatus;
  protected _processingStartTime: Date | null;
  protected _errorCount: number | null;
  protected _totalOperationsCount: number | null;
  protected _error: ProcessingRuntimeError | null;
  protected _uploadId: string | null;

  protected constructor(
    status: ProcessingStatus,
    processingStartTime: Date | null,
    errorCount: number | null,
    totalOperationsCount: number | null,
    error: ProcessingRuntimeError | null,
    uploadId: string | null
  ) {
    this._status = status;
    this._processingStartTime = processingStartTime;
    this._errorCount = errorCount;
    this._totalOperationsCount = totalOperationsCount;
    this._error = error;
    this._uploadId = uploadId;
  }

  get status(): ProcessingStatus {
    return this._status;
  }

  get processingStartTime(): Date | null {
    return this._processingStartTime;
  }

  get errorCount(): number | null {
    return this._errorCount;
  }

  get operationsCount(): number | null {
    return this._totalOperationsCount;
  }

  get error(): ProcessingRuntimeError | null {
    return this._error;
  }

  get uploadId(): string | null {
    return this._uploadId;
  }

  toDoc(): Document {
    return {
      status: this._status,
      processingStartTime: this._processingStartTime,
      errorCount: this._errorCount,
      totalOperationsCount: this._totalOperationsCount,
      error: this._error ? this._error.toDoc() : null,
      uploadId: this._uploadId,
    };
  }

  isOperationOngoing(): boolean {
    return this.isInProcessingState() || this.isInDeletingState();
  }

  isFailed(): boolean {
    return this._status === ProcessingStatus.RUNTIME_ERROR;
  }

  isInErrorState(): boolean {
    return this._status === ProcessingStatus.RUNTIME_ERROR || this._status === ProcessingStatus.PROCESSING_ERROR;
  }

  isInProcessingErrorState(): boolean {
    return this._status === ProcessingStatus.PROCESSING_ERROR;
  }

  isInCancellingState(): boolean {
    return this._status === ProcessingStatus.CANCELLING;
  }

  isInDeletingState(): boolean {
    return this._status === ProcessingStatus.DELETING;
  }

  isInProcessingState(): boolean {
    return this._status === ProcessingStatus.PROCESSING;
  }

  isInIdleState(): boolean {
    return this._status === ProcessingStatus.IDLE;
  }
}
export class Processing extends ProcessingSnapshot {
  get snapshot(): ProcessingSnapshot {
    return new ProcessingSnapshot(
      this._status,
      this._processingStartTime,
      this._errorCount,
      this._totalOperationsCount,
      this._error,
      this._uploadId
    );
  }

  static create(): Processing {
    return new Processing(ProcessingStatus.IDLE, null, null, null, null, null);
  }

  static fromDoc(processingDoc: Document): Processing {
    return new Processing(
      processingDoc ? processingDoc["status"] : ProcessingStatus.IDLE,
      processingDoc ? processingDoc["processingStartTime"] : null,
      processingDoc ? processingDoc["errorCount"] : null,
      processingDoc ? processingDoc["totalOperationsCount"] : null,
      processingDoc && processingDoc["error"] ? ProcessingRuntimeError.fromDoc(processingDoc["error"]) : null,
      processingDoc ? processingDoc["uploadId"] : null
    );
  }

  beginProcessing(uploadId: string): void {
    const transitionStatus = ProcessingStatus.PROCESSING;
    this.verifyStatusTransition(transitionStatus);

    this._status = ProcessingStatus.PROCESSING;
    this._processingStartTime = new Date();
    this._errorCount = null;
    this._totalOperationsCount = null;
    this._error = null;
    this._uploadId = uploadId;
  }

  cancelProcessing(): void {
    const transitionStatus = ProcessingStatus.CANCELLING;
    this.verifyStatusTransition(transitionStatus);

    this._status = transitionStatus;
    this._errorCount = null;
    this._totalOperationsCount = null;
    this._error = null;
  }

  beginDeleting(): void {
    const transitionStatus = ProcessingStatus.DELETING;
    this.verifyStatusTransition(transitionStatus);

    this._status = transitionStatus;
    this._processingStartTime = new Date();
  }

  failProcessing(error: Error): void {
    const transitionStatus = ProcessingStatus.RUNTIME_ERROR;
    this.verifyStatusTransition(transitionStatus);

    this._status = transitionStatus;
    this._errorCount = null;
    this._totalOperationsCount = null;
    this._error = ProcessingRuntimeError.create(error.name, error.message);
  }

  endProcessing(): void {
    const transitionStatus = ProcessingStatus.IDLE;
    this.verifyStatusTransition(transitionStatus);

    this._status = ProcessingStatus.IDLE;
    this._processingStartTime = null;
    this._errorCount = null;
    this._totalOperationsCount = null;
    this._error = null;
    this._uploadId = null;
  }

  endProcessingWithErrors({
    errorCount,
    totalOperationsCount,
  }: {
    errorCount: number;
    totalOperationsCount: number;
  }): void {
    const transitionStatus = ProcessingStatus.PROCESSING_ERROR;
    this.verifyStatusTransition(transitionStatus);

    this._status = ProcessingStatus.PROCESSING_ERROR;
    this._errorCount = errorCount;
    this._totalOperationsCount = totalOperationsCount;
    this._error = null;
  }

  private verifyStatusTransition(newStatus: ProcessingStatus): void {
    const validTransitions = VALID_STATUS_TRANSITIONS[this._status];

    if (!validTransitions.includes(newStatus)) {
      throw new ProcessingInvalidStatusTransitionError(this._status, newStatus);
    }
  }
}
